import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import { PersonRef } from "../types";
import {
  PersonListItem,
  getPersonId,
  getPersonName,
  usePeopleList,
} from "../hooks/usePeopleList";

const PAGE_SIZE = 10;

interface PeoplePickerProps {
  title: string;
  corporateId: string | number;
  roleFilter: string;
  initialSelected: PersonRef[];
  onClose: () => void;
  onConfirm: (people: PersonRef[]) => void;
}

const PeoplePicker: React.FC<PeoplePickerProps> = ({
  title,
  corporateId,
  roleFilter,
  initialSelected,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});
  const [selected, setSelected] = useState<PersonRef[]>(initialSelected);

  const { items, totalItems, totalPages, isLoading } = usePeopleList({
    corporateId,
    roleFilter,
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    fieldFilters,
  });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => String(s.id))),
    [selected],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((p) => getPersonId(p) === String(id));
      if (!found) return;
      setSelected((prev) => [...prev, { id, label: getPersonName(found) }]);
    } else {
      setSelected((prev) => prev.filter((s) => String(s.id) !== String(id)));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 12px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: "8px 24px 16px" }}>
        <SearchableMultiSelectListCst<PersonListItem>
          items={items}
          getId={(p) => getPersonId(p)}
          renderRow={({ item }) => (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {getPersonName(item)}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: theme.palette.text.tertiary,
                }}
              >
                {item.email || `ID: ${getPersonId(item)}`}
              </Typography>
            </Box>
          )}
          selectedIds={selectedSet}
          onToggle={handleToggle}
          initialSearchValue={search}
          onSearchSubmit={(v) => {
            setSearch(v);
            setPage(1);
          }}
          searchPlaceholder={String(t("people-picker.search-placeholder"))}
          searchButtonLabel={String(t("people-picker.search-button"))}
          hideSearch
          headerSlot={
            <FiltersAccordionToggle
              pageId={PagesID["corporate.editions.tutors-faculties"]}
              value={fieldFilters}
              onApply={(next) => {
                setFieldFilters(next);
                setPage(1);
              }}
            />
          }
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={setPage}
          isLoading={isLoading}
          selectedCountLabel={String(
            t("people-picker.selected-count", { count: selected.length }),
          )}
          emptyLabel={String(t("people-picker.empty"))}
        />
      </DialogContent>

      <DialogActions sx={{ padding: "8px 24px 20px" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          }}
        >
          {t("actions.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onConfirm(selected);
            onClose();
          }}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
          }}
        >
          {t("actions.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PeoplePicker;
